'use client';

import Link from 'next/link';
import type { Route } from 'next';
import { motion } from 'framer-motion';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, delay: i * 0.12, ease: [0.25, 0.4, 0.25, 1] as const },
  }),
};

function ProductLink({ href, external, className, children }: { href: string; external?: boolean; className?: string; children: React.ReactNode }) {
  if (external) {
    return <a href={href} target="_blank" rel="noopener noreferrer" className={className}>{children}</a>;
  }
  return <Link href={href as Route} className={className}>{children}</Link>;
}

export default function HeroSection() {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-surface-base">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Animated aurora (brand colors) */}
        <div className="hero-aurora hero-aurora-1" />
        <div className="hero-aurora hero-aurora-2" />
        <div className="hero-aurora hero-aurora-3" />
        <style>{`
          .hero-aurora { position: absolute; border-radius: 9999px; filter: blur(100px); will-change: transform; }
          .hero-aurora-1 { top: -14%; left: -8%; width: 680px; height: 680px; background: radial-gradient(circle, rgba(91,149,255,0.60), transparent 70%); animation: heroAurora1 16s ease-in-out infinite; }
          .hero-aurora-2 { top: 8%; right: -10%; width: 620px; height: 620px; background: radial-gradient(circle, rgba(167,139,250,0.52), transparent 70%); animation: heroAurora2 19s ease-in-out infinite; }
          .hero-aurora-3 { bottom: -20%; left: 22%; width: 580px; height: 580px; background: radial-gradient(circle, rgba(245,165,36,0.34), transparent 70%); animation: heroAurora3 22s ease-in-out infinite; }
          @keyframes heroAurora1 { 0%, 100% { transform: translate(0, 0) scale(1); } 50% { transform: translate(170px, 110px) scale(1.3); } }
          @keyframes heroAurora2 { 0%, 100% { transform: translate(0, 0) scale(1); } 50% { transform: translate(-150px, 90px) scale(1.25); } }
          @keyframes heroAurora3 { 0%, 100% { transform: translate(0, 0) scale(1); } 50% { transform: translate(120px, -100px) scale(1.35); } }
          @media (prefers-reduced-motion: reduce) { .hero-aurora { animation: none !important; } }
        `}</style>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-surface-deepest to-transparent pointer-events-none z-10" />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-16 pb-24">
        <motion.div custom={0} variants={fadeUp} initial="hidden" animate="visible" className="mb-8">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-accent/20 bg-accent/[0.06] text-accent text-xs font-medium tracking-wide uppercase">
            <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
            From the team behind McAfee ePolicy Orchestrator
          </span>
        </motion.div>

        <motion.h1 custom={1} variants={fadeUp} initial="hidden" animate="visible"
          className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight mb-6 max-w-4xl">
          <span className="text-white/90">Scan and fix your</span>
          <br />
          <span className="text-accent">cybersecurity threats.</span>
        </motion.h1>

        <motion.p custom={2} variants={fadeUp} initial="hidden" animate="visible"
          className="text-lg sm:text-xl text-zinc-400 leading-relaxed max-w-2xl mb-16">
          One platform, two layers. A discovery layer that continuously finds what is exposed, and a remediation layer that fixes it and verifies it in production. We sit on the tools you already run and own the last mile.
        </motion.p>

        {/* Product cards */}
        <motion.div custom={3} variants={fadeUp} initial="hidden" animate="visible"
          className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 max-w-5xl mb-16">
          {[
            {
              href: '/autoscan', external: false, label: 'AutoScan', tag: 'Early Access', tagColor: 'emerald',
              headline: 'External Attack Surface Monitoring',
              desc: 'Finds what you actually have exposed. Nightly continuous monitoring and validation, not another checkbox scan. Beta includes AI-suggested fixes.',
              cta: 'Request Beta Access', ctaHref: '/beta-signup?product=AutoScan&source=home_hero',
            },
            {
              href: '/autofix', external: false, label: 'AutoFix', tag: 'Enterprise', tagColor: 'emerald',
              headline: 'Autonomous Remediation',
              desc: 'Sits on the tools you already run and owns the last mile: fixes merged, deployed, and verified in production. Never Break Prod™.',
              cta: 'Request Early Access', ctaHref: '/beta-signup?product=AutoFix&source=home_hero',
            },
            {
              href: 'https://academy.cyberarmy.ai/', external: true, label: 'Academy', tag: 'Live', tagColor: 'emerald',
              headline: 'Security Awareness Training',
              desc: 'Free cybersecurity education: expert-curated and AI-generated quizzes, plain-English threat guides, instant results, and a shareable certificate.',
              cta: 'Visit Academy', ctaHref: 'https://academy.cyberarmy.ai/',
            },
          ].map((p) => (
            <div key={p.label} className={`rounded-2xl border bg-surface-1 p-7 flex flex-col gap-5 ${p.tagColor === 'emerald' ? 'border-accent/20' : 'border-accent-secondary/15'}`}>
              <div className="flex items-start justify-between gap-3">
                <ProductLink href={p.href} external={p.external} className="text-base font-semibold text-white/90 hover:text-accent transition-colors">{p.label}</ProductLink>
                <span className={`text-[10px] font-medium px-2.5 py-1 rounded-full border flex-shrink-0 ${p.tagColor === 'emerald' ? 'bg-accent/8 border-accent/20 text-accent' : 'bg-accent-secondary/8 border-accent-secondary/15 text-accent-secondary'}`}>{p.tag}</span>
              </div>
              <div>
                <p className={`text-xs font-medium mb-2 ${p.tagColor === 'emerald' ? 'text-accent' : 'text-accent-secondary'}`}>{p.headline}</p>
                <p className="text-sm text-zinc-500 leading-relaxed">{p.desc}</p>
              </div>
              <div className="flex items-center gap-4 flex-wrap mt-auto">
                <ProductLink href={p.ctaHref} external={p.external} className={`group inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${p.tagColor === 'emerald' ? 'bg-accent text-black hover:bg-accent-hover shadow-lg shadow-accent/20' : 'border border-accent-secondary/25 text-accent-secondary hover:border-accent-secondary/40 hover:text-accent-secondary-light hover:bg-accent-secondary/[0.06]'}`}>
                  {p.cta}
                  <svg className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
                </ProductLink>
                {!p.external && (
                  <Link href={p.href as Route} className="text-sm font-medium text-zinc-400 hover:text-white transition-colors inline-flex items-center gap-1">
                    Explore {p.label}
                    <span aria-hidden="true">→</span>
                  </Link>
                )}
              </div>
            </div>
          ))}
        </motion.div>

        {/* Stats */}
        <motion.div custom={4} variants={fadeUp} initial="hidden" animate="visible" className="flex flex-wrap gap-8 items-center">
          {[
            { val: '<5 min', label: 'mean time to fix', color: 'text-accent' },
            { val: '<60s', label: 'automated rollback', color: 'text-accent' },
            { val: '20 min', label: 'pentest to report', color: 'text-accent-secondary' },
            { val: '32+ yrs', label: 'endpoint security pedigree', color: 'text-zinc-300' },
          ].map((s) => (
            <div key={s.label} className="flex items-baseline gap-2">
              <span className={`text-2xl font-bold ${s.color}`}>{s.val}</span>
              <span className="text-xs text-zinc-600 uppercase tracking-wider">{s.label}</span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
